import { useEffect } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';

import { Code128Barcode } from '../../components/ui/Code128Barcode';
import { ErrorState, LoadingState } from '../../components/ui/Feedback';
import { useLetterHead } from '../../components/ui/PrintLetterHead';
import { useHospitalTitle } from '../../lib/hospital-brand';
import { useDocumentHeaderLogo } from '../settings/settings.queries';
import { useOpdPrescription, useOpdRegistration } from './opd.queries';
import { formatOpdDateTime } from './opd.utils';

const PRESCRIPTION_HEADER_NAME = 'opd_prescription';
const upper = (value: string | number | null | undefined) => String(value ?? '').toUpperCase();

export const OpdPrescriptionPrintPage = () => {
  const { registrationId } = useParams();
  const [params] = useSearchParams();
  const withHeader = params.get('header') === '1';
  const hospitalTitle = useHospitalTitle();
  const query = useOpdRegistration(registrationId);
  const prescriptionQuery = useOpdPrescription(registrationId);
  const headerLogoQuery = useDocumentHeaderLogo(PRESCRIPTION_HEADER_NAME, withHeader);
  const letterHead = useLetterHead(withHeader);

  useEffect(() => {
    if (query.data && prescriptionQuery.data && (!withHeader || (headerLogoQuery.isFetched && letterHead.isReady))) window.setTimeout(() => window.print(), 300);
  }, [query.data, prescriptionQuery.data, withHeader, headerLogoQuery.isFetched, letterHead.isReady]);

  if (query.isLoading || prescriptionQuery.isLoading) return <LoadingState />;
  if (query.isError || !query.data) return <ErrorState title="OPD registration not found" message="The registration could not be loaded." />;
  if (prescriptionQuery.isError || !prescriptionQuery.data) return <ErrorState title="OPD prescription not found" message="The prescription could not be loaded." />;

  const row = query.data;
  const patient = row.patientSnapshot;
  const logo = headerLogoQuery.data ?? letterHead.imageUrl;
  const age = [
    patient.ageYears ? `${patient.ageYears}Y` : '',
    patient.ageMonths ? `${patient.ageMonths}M` : '',
    patient.ageDays ? `${patient.ageDays}D` : '',
  ].filter(Boolean).join(' ');
  const address = [patient.address, patient.district, patient.state, patient.pinCode].filter(Boolean).join(',');
  const doctorName = row.doctor.displayName || row.doctor.name;
  const doctorPrintName = upper(doctorName).replace(/^(DR\.?\s+)+/, 'DR. ');
  const savedVitals = prescriptionQuery.data.vitals[0];
  const bloodPressure = savedVitals?.bpSystolic || savedVitals?.bpDiastolic
    ? `${savedVitals?.bpSystolic ?? ''}/${savedVitals?.bpDiastolic ?? ''}`
    : '';
  const vitals = [
    `Height : ${savedVitals?.height ?? ''}`,
    `Weight : ${savedVitals?.weight ?? ''}`,
    `BP : ${bloodPressure}`,
    `Temp : ${savedVitals?.temperature ?? ''}`,
    `Pulse : ${savedVitals?.pulse ?? ''}`,
    'RR : ',
  ];

  return (
    <div className="print-prescription-page">
      <div className="print-prescription-toolbar no-print">
        <button type="button" onClick={() => window.print()}>PRINT</button>
        <button type="button" className="danger" onClick={() => window.close()}>CLOSE</button>
      </div>

      {withHeader && logo ? (
        <table className="print-prescription-logo-table"><tbody><tr><td><img src={logo} alt={hospitalTitle} /></td></tr></tbody></table>
      ) : (
        <div className="print-prescription-header-space" aria-hidden="true" />
      )}

      <table className="print-prescription-info-row"><tbody><tr>
        <td className="print-prescription-info-cell">DATE : {upper(formatOpdDateTime(row.appointmentDate))}</td>
        <td className="print-prescription-info-cell print-prescription-uhid-cell">
          {patient.uhid ? <Code128Barcode value={patient.uhid} /> : null}
        </td>
        <td className="print-prescription-info-cell">OPD NO : {row.opdNumber}</td>
        <td className="print-prescription-info-cell">TOKEN NO : {row.ticketNumber}</td>
      </tr></tbody></table>

      <table className="print-prescription-details"><tbody><tr>
        <td className="print-prescription-patient-cell">
          <p>PATIENT NAME : <b>{upper(patient.name)}</b></p>
          <p>GENDER : <b>{upper(patient.gender)}</b> || AGE : <b>{age}</b></p>
          <p>MOB NO : <b>{upper(patient.phone)}</b></p>
          <p>ADDRESS : {upper(address)}</p>
        </td>
        <td className="print-prescription-doctor-cell">
          <p>CONSULTANT DOCTOR : {doctorPrintName}</p>
          <p>{upper(row.doctor.specialization)}</p>
          <p>{upper(row.doctor.qualification)}</p>
          <p>REG NO - {upper(row.doctor.registrationNumber)}</p>
        </td>
      </tr></tbody></table>

      <table className="print-prescription-vitals"><tbody><tr>
        {vitals.map((label) => <td key={label}><b>{upper(label)}</b></td>)}
      </tr></tbody></table>
    </div>
  );
};
